import { createRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { Archive, ShieldCheck, Share2, Monitor, Globe, Heart, HardDrive, DollarSign, Laptop, Users, Radio, Camera, Watch, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { useAppMode } from '../context/AppModeContext';
import { usePreferences, applyTheme, setThemeOverride } from '../hooks/usePreferences';
import { useTranslation } from '../i18n';
import type { Locale } from '../i18n';
import { Button } from '../components/ui/Button';
import { CategoryIcon } from '../components/ui/CategoryIcon';
import { KOFI_URL } from '../constants/external';
import { Route as rootRoute } from './__root';
import styles from './index.module.css';

function AppMockup() {
  const items = [
    { label: 'Commodore 64', badge: 'Mint', image: '/images/landing/retro-computer.jpg' },
    { label: 'Leica M3', badge: 'Good', image: '/images/landing/vintage-camera.jpg' },
    { label: 'Omega Seamaster', badge: 'Mint', image: '/images/landing/luxury-watch.jpg' },
  ];
  return (
    <div className={styles.mockupWrap}>
      <div className={styles.mockup}>
        <div className={styles.mockupBar}>
          <div className={styles.mockupDots}>
            <span className={`${styles.mockupDot} ${styles.mockupDotRed}`} />
            <span className={`${styles.mockupDot} ${styles.mockupDotYellow}`} />
            <span className={`${styles.mockupDot} ${styles.mockupDotGreen}`} />
          </div>
          <span className={styles.mockupTitle}>SiliconVault</span>
        </div>
        <div className={styles.mockupContent}>
          {items.map(({ label, badge, image }) => (
            <div key={label} className={styles.mockupCard}>
              <img src={image} alt={label} className={styles.mockupCardImage} loading="lazy" />
              <div className={styles.mockupCardBody}>
                <div className={styles.mockupCardLabel}>{label}</div>
                <span className={styles.mockupCardBadge}>{badge}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

function LandingPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { mode } = useAppMode();
  const { preferences, setPreference } = usePreferences();
  const { t, locale } = useTranslation();
  const { data: communityStats } = useQuery<{ collectors: number; items: number }>({
    queryKey: ['community-stats'],
    queryFn: () => fetch('/api/community/stats').then((r) => r.json()),
    staleTime: 5 * 60 * 1000,
  });

  // Force Vivid theme on landing page, restore user's theme on leave
  useEffect(() => {
    setThemeOverride(true);
    applyTheme('vivid');
    return () => {
      setThemeOverride(false);
      applyTheme(preferences.theme);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Authenticated users go straight to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/dashboard', replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Desktop: skip landing entirely — Shell handles auth wait
  if (mode === 'desktop') {
    return <Navigate to="/dashboard" replace />;
  }

  const features = [
    {
      Icon: Archive,
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc'),
    },
    {
      Icon: ShieldCheck,
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc'),
    },
    {
      Icon: Share2,
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc'),
    },
  ];

  const toggleLocale = (next: Locale) => setPreference('locale', next);

  return (
    <div className={styles.page}>
      <div className={styles.controls}>
        {/* Language switcher */}
        <div className={styles.langPill} role="group" aria-label="Language">
          <Globe size={13} strokeWidth={1.75} className={styles.langIcon} />
          {(['en', 'he'] as Locale[]).map((loc) => (
            <button
              key={loc}
              className={`${styles.langOption} ${locale === loc ? styles.langOptionActive : ''}`}
              onClick={() => toggleLocale(loc)}
              aria-pressed={locale === loc}
            >
              {loc.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            <span className={styles.heroPrompt}>&gt; </span>
            SiliconVault
          </h1>
          <p className={styles.heroHeadline}>
            {t('landing.heroHeadline')}{' '}
            <span className={styles.accent}>{t('landing.heroAccent')}</span>
            {t('landing.heroHeadlineSuffix')}
          </p>
          <p className={styles.heroSub}>
            {t('landing.heroSub')}
          </p>
          <div className={styles.heroCta}>
            {!isLoading && (
              isAuthenticated ? (
                <Link to="/dashboard">
                  <Button>{t('landing.goToDashboard')}</Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button>{t('landing.getStarted')}</Button>
                </Link>
              )
            )}
          </div>

          {communityStats && communityStats.collectors > 0 && (
            <p className={styles.socialProof}>
              <Users size={13} strokeWidth={1.75} />
              {t('landing.socialProof')
                .replace('{collectors}', String(communityStats.collectors))
                .replace('{items}', String(communityStats.items))}
            </p>
          )}

          <AppMockup />
        </div>
      </section>

      {/* Features */}
      <section id="features" className={styles.features}>
        <h2 className={styles.featuresTitle}>{t('landing.featuresTitle')}</h2>
        <div className={styles.featureGrid}>
          {features.map(({ Icon, title, description }) => (
            <div key={title} className={styles.featureCard}>
              <div className={styles.featureIconWrap}>
                <CategoryIcon icon={Icon} size="lg" />
              </div>
              <h3 className={styles.featureCardTitle}>{title}</h3>
              <p className={styles.featureCardDesc}>{description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Vault Types */}
      <section className={styles.vaultsSection}>
        <h2 className={styles.vaultsTitle}>{t('landing.vaultsTitle')}</h2>
        <p className={styles.vaultsSub}>{t('landing.vaultsSub')}</p>
        <div className={styles.vaultsGrid}>
          {[
            { Icon: Monitor, title: t('landing.vaultRetroComputers'), desc: t('landing.vaultRetroComputersDesc'), image: '/images/landing/retro-computer.jpg' },
            { Icon: Camera, title: t('landing.vaultVintageCameras'), desc: t('landing.vaultVintageCamerasDesc'), image: '/images/landing/vintage-camera.jpg' },
            { Icon: Watch, title: t('landing.vaultWatches'), desc: t('landing.vaultWatchesDesc'), image: '/images/landing/luxury-watch.jpg' },
            { Icon: Radio, title: t('landing.vaultRetroAudio'), desc: t('landing.vaultRetroAudioDesc'), image: '/images/landing/turntable.jpg' },
          ].map(({ Icon, title, desc, image }) => (
            <div key={title} className={styles.vaultCard}>
              <img src={image} alt={title} className={styles.vaultCardImage} loading="lazy" />
              <div className={styles.vaultIconWrap}>
                <CategoryIcon icon={Icon} size="lg" />
              </div>
              <h3 className={styles.vaultCardTitle}>{title}</h3>
              <p className={styles.vaultCardDesc}>{desc}</p>
            </div>
          ))}
          <div className={`${styles.vaultCard} ${styles.vaultCardCustom}`}>
            <div className={styles.vaultIconWrap} style={{ marginTop: 0 }}>
              <CategoryIcon icon={Sparkles} size="xl" />
            </div>
            <h3 className={styles.vaultCardTitle}>{t('vaults.customCard.title')}</h3>
            <p className={styles.vaultCardDesc}>{t('vaults.customCard.tagline' as never)}</p>
          </div>
        </div>
      </section>

      {/* Desktop App */}
      <section className={styles.desktopSection}>
        <h2 className={styles.desktopTitle}>{t('landing.desktopTitle')}</h2>
        <p className={styles.desktopSub}>{t('landing.desktopSub')}</p>
        <div className={styles.desktopGrid}>
          {[
            { Icon: HardDrive, title: t('landing.desktopFeature1Title'), desc: t('landing.desktopFeature1Desc') },
            { Icon: DollarSign, title: t('landing.desktopFeature2Title'), desc: t('landing.desktopFeature2Desc') },
            { Icon: Laptop, title: t('landing.desktopFeature3Title'), desc: t('landing.desktopFeature3Desc') },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className={styles.desktopCard}>
              <div className={styles.desktopIconWrap}>
                <CategoryIcon icon={Icon} size="lg" />
              </div>
              <h3 className={styles.desktopCardTitle}>{title}</h3>
              <p className={styles.desktopCardDesc}>{desc}</p>
            </div>
          ))}
        </div>
        <div className={styles.desktopCta}>
          <Link to="/download">
            <Button>{t('landing.desktopCta')}</Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <span className={styles.footerText}>
          &copy; {new Date().getFullYear()} Asaf Saar. {t('landing.footer')}
        </span>
        <span className={styles.footerSep}> | </span>
        <Link to="/pricing" className={styles.footerLink}>{t('pricing.footerLink')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/terms" className={styles.footerLink}>{t('footer.terms')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/privacy" className={styles.footerLink}>{t('footer.privacy')}</Link>
        <span className={styles.footerSep}> | </span>
        <Link to="/refund" className={styles.footerLink}>{t('footer.refund')}</Link>
        <span className={styles.footerSep}> | </span>
        <a
          href={KOFI_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.footerSupport}
        >
          <Heart size={10} strokeWidth={2} />
          {t('support.footerLink')}
        </a>
      </footer>
    </div>
  );
}
